'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { trackWalkthroughCheckpoint } from '@/lib/bookmarks';
import { HandedALensMoment } from '@/app/open-letter/components/Plot3MuralEnhancements';

interface Checkpoint {
  id: string;
  stage: string;
  stageAr: string;
  stageNum: string;
  title: string;
  subtitle: string;
  color: string;
  href: string;
  description: string;
  explanation: string;
  keyTerms: { term: string; def: string }[];
  verse?: { arabic: string; translation: string; ref: string };
  completed?: boolean;
}

const CHECKPOINTS: Checkpoint[] = [
  {
    id: 'awareness',
    stage: 'AWARENESS',
    stageAr: 'الوعي',
    stageNum: '01',
    title: 'Open Letter: Deep Green',
    subtitle: 'The Invitation',
    color: '#D4A82A',
    href: '/open-letter',
    description: 'The journey begins with an open letter — a direct address to the caretaker. Before any tool, before any framework, there is a recognition: you are already in the middle of something sacred.',
    explanation: 'The Open Letter is not an introduction to a curriculum. It is an acknowledgment that the caretaker already carries the Names of Allah in their daily work — in the way they tend to people, places, and patterns. Awareness is the first stage: seeing that what you do is already connected to something larger.',
    keyTerms: [
      { term: 'Haal', def: 'State of being — the condition of the caretaker and the community they serve.' },
      { term: 'Kulli Haal', def: 'Every state — the recognition that all conditions are addressed by the Names.' },
      { term: 'Amanah', def: 'Trust — the sacred responsibility the caretaker carries.' },
    ],
    verse: {
      arabic: 'سَنُرِيهِمْ آيَاتِنَا فِي الْآفَاقِ وَفِي أَنفُسِهِمْ',
      translation: 'Soon We will show them Our signs in the horizons and in themselves',
      ref: '41:53',
    },
  },
  {
    id: 'orientation',
    stage: 'ORIENTATION',
    stageAr: 'التوجيه',
    stageNum: '02',
    title: 'Guided Intro',
    subtitle: 'The Framework',
    color: '#2d8a87',
    href: '/guided-intro',
    description: 'The Guided Intro orients the caretaker to the framework — the Five Haals, the Keys, the Ayat, and the IRL Lens. This is the map before the territory.',
    explanation: 'Orientation is not memorization. It is the act of situating yourself within a structure that already exists. The framework is not invented — it is recognized. The Five Haals are not categories imposed on reality; they are patterns already present in creation, now named.',
    keyTerms: [
      { term: 'Fitra', def: 'Original design — the innate nature that recognizes truth.' },
      { term: 'Taqwah', def: 'God-consciousness — the quality of awareness that the framework cultivates.' },
      { term: 'Furqan', def: 'Discernment — the Quran as the ultimate tool for distinguishing truth from falsehood.' },
    ],
    verse: {
      arabic: 'فَأَقِمْ وَجْهَكَ لِلدِّينِ حَنِيفًا ۚ فِطْرَتَ اللَّهِ الَّتِي فَطَرَ النَّاسَ عَلَيْهَا',
      translation: 'So direct your face toward the religion, inclining to truth. The nature of Allah upon which He has created people.',
      ref: '30:30',
    },
  },
  {
    id: 'keys',
    stage: 'KEYS',
    stageAr: 'المفاتيح',
    stageNum: '03',
    title: 'Keys to Kulli Haal',
    subtitle: 'The Curriculum',
    color: '#D4A82A',
    href: '/keys-to-kulli-haal',
    description: 'The Keys are the curriculum — seven conceptual frameworks that unlock the caretaker\'s ability to read creation as Ayat. Each Key is a lens, a vocabulary, a practice.',
    explanation: 'The Keys are not lessons to be completed — they are tools to be carried. Key 01 (Ayat) teaches the caretaker to read creation as signs. Key 03 (Taqwah) introduces the quality spectrum from awareness to Ihsan. Key 05 (Amanah) frames the caretaker\'s role as trustee of creation. Each Key builds on the last, but all are present simultaneously in every encounter.',
    keyTerms: [
      { term: 'Wujud', def: 'Existence — the foundational recognition that everything that exists points to Al-Wajid.' },
      { term: 'Ihsan', def: 'Excellence — the highest degree of God-consciousness in work, as if you see Allah.' },
      { term: 'Falah', def: 'Flourishing — cultivation, not accumulation. The reframing of success.' },
    ],
    verse: {
      arabic: 'وَالْعَصْرِ ۝ إِنَّ الْإِنسَانَ لَفِي خُسْرٍ ۝ إِلَّا الَّذِينَ آمَنُوا وَعَمِلُوا الصَّالِحَاتِ',
      translation: 'By time — indeed, mankind is in loss, except those who believe and do righteous deeds.',
      ref: '103:1-3',
    },
  },
  {
    id: 'ayat',
    stage: 'AYAT',
    stageAr: 'الآيات',
    stageNum: '04',
    title: 'Ayat in Creation',
    subtitle: 'The Open World',
    color: '#2d7a47',
    href: '/ayat-in-creation',
    description: 'Ayat in Creation is the open-world journey — the caretaker moves from the classroom into the field, reading the signs of Allah in ecosystems, communities, and the patterns of the world.',
    explanation: 'The Ayat section is where the framework becomes practice. The caretaker learns to ask three questions of every encounter: Is it? What is it? When is it? These are the three examination questions of the IRL Lens, practiced first in the open world before being formalized in the tool.',
    keyTerms: [
      { term: 'Ayah', def: 'Sign — every observable thing in creation that points back to its Creator.' },
      { term: 'Zahir', def: 'The apparent — what is immediately observable in the encounter.' },
      { term: 'Batin', def: 'The hidden — the deeper pattern or principle beneath the surface.' },
    ],
    verse: {
      arabic: 'أَوَلَمْ يَنظُرُوا فِي مَلَكُوتِ السَّمَاوَاتِ وَالْأَرْضِ',
      translation: 'Have they not looked into the realm of the heavens and the earth?',
      ref: '7:185',
    },
  },
  {
    id: 'names',
    stage: 'NAMES',
    stageAr: 'الأسماء',
    stageNum: '05',
    title: 'Names Reference',
    subtitle: 'The Vocabulary',
    color: '#8a8ab8',
    href: '/names-reference',
    description: 'The Names Reference is the vocabulary of the framework — 99 Names of Allah, each a lens filter for reading creation. The caretaker learns to see the Names in what they encounter.',
    explanation: 'The Names are not a list to memorize — they are a living vocabulary. Al-Khaliq is visible in every act of creation. Ar-Razzaq is visible in every provision. Al-Adl is visible in every act of justice. The Names Reference gives the caretaker the words to name what they already see.',
    keyTerms: [
      { term: 'Yaqeen', def: 'Certainty — the fruit of aligned Basar (outer sight) and Basirah (inner sight).' },
      { term: 'Basar', def: 'Outer sight — the physical capacity to observe creation.' },
      { term: 'Basirah', def: 'Inner sight — the spiritual capacity to recognize the Names in what is observed.' },
    ],
    verse: {
      arabic: 'هُوَ اللَّهُ الَّذِي لَا إِلَٰهَ إِلَّا هُوَ ۖ الْمَلِكُ الْقُدُّوسُ السَّلَامُ',
      translation: 'He is Allah, other than whom there is no deity — the Sovereign, the Pure, the Perfection.',
      ref: '59:23',
    },
  },
  {
    id: 'recognition',
    stage: 'RECOGNITION',
    stageAr: 'المعرفة',
    stageNum: '06',
    title: 'Inner Reality Lens',
    subtitle: 'The Tool',
    color: '#D4A82A',
    href: '/inner-reality-lens',
    description: 'The IRL Lens is the application tool — the formalization of the recognition arc into a repeatable methodology. The caretaker moves from aware to conscious.',
    explanation: 'The IRL Lens is not a new way of seeing — it is a disciplined practice of what the caretaker already does. The recognition arc (Mata → Encounter → Examination → Recognition → Re-Assembly → Uboor) is the structure of every meaningful encounter with creation. The Lens makes this structure explicit, repeatable, and teachable.',
    keyTerms: [
      { term: 'Mata', def: 'The lens as tool — the starting point of every recognition arc.' },
      { term: 'Uboor', def: 'Crossing over — the movement from recognition to action, from seeing to doing.' },
      { term: 'Islah', def: 'Reform/Salah — the act of setting things right, rooted in the same root as prayer.' },
    ],
    verse: {
      arabic: 'سَنُرِيهِمْ آيَاتِنَا فِي الْآفَاقِ وَفِي أَنفُسِهِمْ حَتَّىٰ يَتَبَيَّنَ لَهُمْ أَنَّهُ الْحَقُّ',
      translation: 'Soon We will show them Our signs in the horizons and in themselves until it becomes clear that it is the Truth.',
      ref: '41:53',
    },
  },
  {
    id: 'action',
    stage: 'ACTION',
    stageAr: 'العمل',
    stageNum: '07',
    title: 'Muslim Resource Guide',
    subtitle: 'The Caretaking',
    color: '#2d7a47',
    href: '/muslim-resource-guide',
    description: 'The Muslim Resource Guide is where recognition becomes action — a living directory of community resources rooted in the Rights of Creation. The caretaker moves from seeing to doing.',
    explanation: 'The Resource Guide is not a list of services — it is a map of Amanah. Every resource in the guide exists because creation has rights. Housing, education, health, and justice are not social services — they are Ayat. The caretaker who has walked the recognition arc arrives here ready to act, not just to observe.',
    keyTerms: [
      { term: 'Haqq Al-Khalq', def: 'The right of creation — every created thing carries rights by virtue of being created.' },
      { term: 'Fisabilillah', def: 'In the path of Allah — the orientation of all caretaking action.' },
      { term: 'Mizan', def: 'Balance/scale — the divine measure that the caretaker works to restore.' },
    ],
    verse: {
      arabic: 'وَلَقَدْ كَرَّمْنَا بَنِي آدَمَ',
      translation: 'And We have certainly honored the children of Adam.',
      ref: '17:70',
    },
  },
];

// ─── LEAF SEQUENCE ENCOUNTER ──────────────────────────────────────────────────

const LEAF_NAMES = [
  { name: 'Al-Khaliq', arabic: 'الْخَالِق', meaning: 'The Creator', connection: 'The leaf\'s form — its veins, its shape, its precise structure — is the signature of Al-Khaliq. Every leaf is a unique creation.' },
  { name: 'Al-Musawwir', arabic: 'الْمُصَوِّر', meaning: 'The Fashioner of Forms', connection: 'The specific form of this leaf — its lobes, its margins, its surface texture — is the work of Al-Musawwir, the One who fashions every form distinctly.' },
  { name: 'Ar-Razzaq', arabic: 'الرَّزَّاق', meaning: 'The Provider', connection: 'Photosynthesis — the leaf converting light into provision — is Ar-Razzaq made visible. The leaf is a provision factory, and the provision is not its own.' },
  { name: 'Al-Latif', arabic: 'اللَّطِيف', meaning: 'The Subtle / The Gentle', connection: 'The microscopic processes within the leaf — the movement of water through stomata, the exchange of gases — are Al-Latif: the subtle, invisible workings beneath the surface.' },
  { name: 'Al-Muhyi', arabic: 'الْمُحْيِي', meaning: 'The Giver of Life', connection: 'The leaf\'s role in the oxygen cycle — giving life to the atmosphere — is Al-Muhyi made manifest. The leaf gives life without knowing it gives life.' },
];

function LeafSequenceEncounter() {
  const [step, setStep] = useState<'intro' | 'q1' | 'q2' | 'q3' | 'names' | 'complete'>('intro');
  const [answers, setAnswers] = useState({ q1: '', q2: '', q3: '' });
  const [selectedName, setSelectedName] = useState<string | null>(null);
  const [nameNote, setNameNote] = useState('');

  return (
    <div className="rounded-xl overflow-hidden" style={{ background: 'rgba(6,13,18,0.8)', border: '1px solid rgba(45,122,71,0.3)' }}>
      {/* Header */}
      <div className="px-6 py-5" style={{ background: 'rgba(45,122,71,0.08)', borderBottom: '1px solid rgba(45,122,71,0.15)' }}>
        <div className="flex items-center gap-3 mb-1">
          <span style={{ color: '#2d7a47', fontSize: 18 }}>☽</span>
          <p className="font-mono text-[10px] uppercase tracking-widest" style={{ color: '#2d7a47' }}>
            FIRST LEAF SEQUENCE ENCOUNTER · تسلسل الورقة الأول
          </p>
        </div>
        <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 12, marginTop: 4 }}>
          A guided first encounter with the IRL Lens methodology — from observation to Name extraction.
        </p>
      </div>

      <div className="p-6">
        {step === 'intro' && (
          <div>
            <div className="mb-6 p-5 rounded-xl" style={{ background: 'rgba(45,122,71,0.06)', border: '1px solid rgba(45,122,71,0.15)' }}>
              <p style={{ fontFamily: "'Amiri', serif", fontSize: 22, color: '#c9a84c', direction: 'rtl', marginBottom: 8, textAlign: 'right' }}>
                وَفِي الْأَرْضِ آيَاتٌ لِّلْمُوقِنِينَ
              </p>
              <p className="italic" style={{ color: '#d4c5a9', fontSize: 13 }}>
                "And in the earth are signs for those who are certain." — (Quran 51:20)
              </p>
            </div>
            <p style={{ color: '#d4c5a9', fontSize: 14, lineHeight: 1.85, marginBottom: 16 }}>
              Find a leaf. Any leaf — from a tree, a plant, a crack in the sidewalk. Hold it or observe it closely. This is your first Mata: the tool, the encounter, the beginning of the recognition arc.
            </p>
            <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 13, lineHeight: 1.75, marginBottom: 24 }}>
              You are about to ask three questions that the IRL Lens asks of every created thing. These are not new questions — they are the questions of every scientist, every poet, every child who has ever held something and wondered. The framework gives them a name.
            </p>
            <div className="flex gap-3 flex-wrap">
              <button
                onClick={() => setStep('q1')}
                className="font-mono uppercase tracking-widest px-6 py-3 transition-all duration-200"
                style={{ background: '#2d7a47', color: '#f5f0e8', fontSize: 11, letterSpacing: '0.15em', border: 'none', cursor: 'pointer' }}
              >
                Begin the Encounter →
              </button>
            </div>
          </div>
        )}

        {step === 'q1' && (
          <div>
            <div className="mb-5 flex items-center gap-3">
              <div className="w-8 h-8 rounded-full flex items-center justify-center font-mono text-sm font-black" style={{ background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.4)', color: '#c9a84c' }}>1</div>
              <div>
                <p className="font-mono uppercase tracking-widest" style={{ color: '#c9a84c', fontSize: 12 }}>IS IT? · هل هو موجود؟</p>
                <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 11 }}>The Question of Wujud — Existence</p>
              </div>
            </div>
            <p style={{ color: '#d4c5a9', fontSize: 14, lineHeight: 1.8, marginBottom: 8 }}>
              Look at the leaf. Confirm its existence. This is not trivial — the Quran asks us to look at creation and confirm: it is here. It exists. Its existence is not necessary — it could have not existed. But it does.
            </p>
            <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 12, lineHeight: 1.7, marginBottom: 16 }}>
              Huduth Dhati: the leaf's existence is contingent — it depends on something else for its existence. It did not bring itself into being. This is the first recognition.
            </p>
            <textarea
              value={answers.q1}
              onChange={e => setAnswers(a => ({ ...a, q1: e.target.value }))}
              placeholder="Describe what you observe existing. What is here? What confirms its presence? (Optional — this is for your own reflection)"
              className="w-full rounded-lg p-4 font-mono text-sm resize-none focus:outline-none"
              rows={4}
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(201,168,76,0.15)', color: '#d4c5a9', lineHeight: 1.6 }}
            />
            <div className="flex gap-3 mt-4">
              <button onClick={() => setStep('intro')} className="font-mono text-xs tracking-widest px-4 py-2 transition-all" style={{ border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(212,197,169,0.4)', background: 'transparent', cursor: 'pointer' }}>← Back</button>
              <button onClick={() => setStep('q2')} className="font-mono uppercase tracking-widest px-6 py-3 transition-all" style={{ background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.3)', color: '#c9a84c', fontSize: 11, cursor: 'pointer' }}>Continue →</button>
            </div>
          </div>
        )}

        {step === 'q2' && (
          <div>
            <div className="mb-5 flex items-center gap-3">
              <div className="w-8 h-8 rounded-full flex items-center justify-center font-mono text-sm font-black" style={{ background: 'rgba(45,138,135,0.15)', border: '1px solid rgba(45,138,135,0.4)', color: '#2d8a87' }}>2</div>
              <div>
                <p className="font-mono uppercase tracking-widest" style={{ color: '#2d8a87', fontSize: 12 }}>WHAT IS IT? · ما هو؟</p>
                <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 11 }}>The Question of Mahiyya — Essence</p>
              </div>
            </div>
            <p style={{ color: '#d4c5a9', fontSize: 14, lineHeight: 1.8, marginBottom: 8 }}>
              Now look deeper. What is the leaf essentially? Not just "a leaf" — what are its qualities, its structure, its function? What makes it what it is, distinct from everything else?
            </p>
            <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 12, lineHeight: 1.7, marginBottom: 16 }}>
              Consider: its color, its veins (the vascular system), its surface (smooth, rough, waxy), its shape (lobed, serrated, entire), its size. Each quality is a signature — a mark of Al-Musawwir, the Fashioner of Forms.
            </p>
            <textarea
              value={answers.q2}
              onChange={e => setAnswers(a => ({ ...a, q2: e.target.value }))}
              placeholder="What are the essential qualities of this leaf? What makes it what it is? Describe its Zahir (apparent) qualities..."
              className="w-full rounded-lg p-4 font-mono text-sm resize-none focus:outline-none"
              rows={4}
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(45,138,135,0.15)', color: '#d4c5a9', lineHeight: 1.6 }}
            />
            <div className="flex gap-3 mt-4">
              <button onClick={() => setStep('q1')} className="font-mono text-xs tracking-widest px-4 py-2 transition-all" style={{ border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(212,197,169,0.4)', background: 'transparent', cursor: 'pointer' }}>← Back</button>
              <button onClick={() => setStep('q3')} className="font-mono uppercase tracking-widest px-6 py-3 transition-all" style={{ background: 'rgba(45,138,135,0.1)', border: '1px solid rgba(45,138,135,0.3)', color: '#2d8a87', fontSize: 11, cursor: 'pointer' }}>Continue →</button>
            </div>
          </div>
        )}

        {step === 'q3' && (
          <div>
            <div className="mb-5 flex items-center gap-3">
              <div className="w-8 h-8 rounded-full flex items-center justify-center font-mono text-sm font-black" style={{ background: 'rgba(45,122,71,0.15)', border: '1px solid rgba(45,122,71,0.4)', color: '#2d7a47' }}>3</div>
              <div>
                <p className="font-mono uppercase tracking-widest" style={{ color: '#2d7a47', fontSize: 12 }}>WHEN IS/WAS IT? · متى كان؟</p>
                <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 11 }}>The Question of Huduth Zamani — Temporal Contingency</p>
              </div>
            </div>
            <p style={{ color: '#d4c5a9', fontSize: 14, lineHeight: 1.8, marginBottom: 8 }}>
              Now consider the sequence that brought this leaf into being. It did not appear from nothing — there was a seed, a root, a season of rain, years of growth. What is the temporal chain that produced this leaf?
            </p>
            <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 12, lineHeight: 1.7, marginBottom: 16 }}>
              Huduth Zamani: the leaf has a beginning in time. It was not always here. This temporal contingency is itself an Ayah — a sign pointing to the One who is Al-Awwal (The First) and Al-Akhir (The Last), who has no beginning and no end.
            </p>
            <textarea
              value={answers.q3}
              onChange={e => setAnswers(a => ({ ...a, q3: e.target.value }))}
              placeholder="What sequence brought this leaf into being? What came before it? What will come after? Trace the Sunnat Allah — the pattern of Allah — in its temporal existence..."
              className="w-full rounded-lg p-4 font-mono text-sm resize-none focus:outline-none"
              rows={4}
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(45,122,71,0.15)', color: '#d4c5a9', lineHeight: 1.6 }}
            />
            <div className="flex gap-3 mt-4">
              <button onClick={() => setStep('q2')} className="font-mono text-xs tracking-widest px-4 py-2 transition-all" style={{ border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(212,197,169,0.4)', background: 'transparent', cursor: 'pointer' }}>← Back</button>
              <button onClick={() => setStep('names')} className="font-mono uppercase tracking-widest px-6 py-3 transition-all" style={{ background: 'rgba(45,122,71,0.1)', border: '1px solid rgba(45,122,71,0.3)', color: '#2d7a47', fontSize: 11, cursor: 'pointer' }}>Extract Names →</button>
            </div>
          </div>
        )}

        {step === 'names' && (
          <div>
            <div className="mb-5">
              <p className="font-mono uppercase tracking-widest mb-1" style={{ color: '#c9a84c', fontSize: 12 }}>NAME EXTRACTION · استخراج الأسماء</p>
              <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 12 }}>Which Names of Allah are active in what you observed?</p>
            </div>
            <p style={{ color: '#d4c5a9', fontSize: 13, lineHeight: 1.8, marginBottom: 20 }}>
              You have asked three questions of the leaf. Now: which Names of Allah are visible in what you found? Each quality you observed — the form, the provision, the sequence — points to a Name. Select the Name that resonates most with your encounter.
            </p>
            <div className="grid grid-cols-1 gap-3 mb-6">
              {LEAF_NAMES.map(n => (
                <button
                  key={n.name}
                  onClick={() => setSelectedName(n.name)}
                  className="text-left rounded-xl p-4 transition-all duration-200"
                  style={{
                    background: selectedName === n.name ? 'rgba(201,168,76,0.1)' : 'rgba(255,255,255,0.02)',
                    border: `1px solid ${selectedName === n.name ? 'rgba(201,168,76,0.5)' : 'rgba(255,255,255,0.06)'}`,
                  }}
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <p style={{ fontFamily: "'Amiri', serif", fontSize: 20, color: selectedName === n.name ? '#c9a84c' : 'rgba(201,168,76,0.4)', direction: 'rtl' }}>{n.arabic}</p>
                    </div>
                    <div className="flex-1">
                      <p className="font-mono text-[10px] uppercase tracking-widest mb-1" style={{ color: selectedName === n.name ? '#c9a84c' : 'rgba(212,197,169,0.5)' }}>{n.name} — {n.meaning}</p>
                      <p style={{ color: 'rgba(212,197,169,0.55)', fontSize: 11, lineHeight: 1.6 }}>{n.connection}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
            {selectedName && (
              <div className="mb-5">
                <p className="font-mono text-[10px] uppercase tracking-widest mb-2" style={{ color: 'rgba(212,197,169,0.4)' }}>
                  How did you see {selectedName} in this leaf? (Optional)
                </p>
                <textarea
                  value={nameNote}
                  onChange={e => setNameNote(e.target.value)}
                  placeholder={`Describe how ${selectedName} was visible in your encounter...`}
                  className="w-full rounded-lg p-4 font-mono text-sm resize-none focus:outline-none"
                  rows={3}
                  style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(201,168,76,0.15)', color: '#d4c5a9', lineHeight: 1.6 }}
                />
              </div>
            )}
            <div className="flex gap-3">
              <button onClick={() => setStep('q3')} className="font-mono text-xs tracking-widest px-4 py-2 transition-all" style={{ border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(212,197,169,0.4)', background: 'transparent', cursor: 'pointer' }}>← Back</button>
              <button
                onClick={() => setStep('complete')}
                disabled={!selectedName}
                className="font-mono uppercase tracking-widest px-6 py-3 transition-all"
                style={{ background: selectedName ? '#c9a84c' : 'rgba(201,168,76,0.1)', color: selectedName ? '#0a1a12' : 'rgba(201,168,76,0.3)', fontSize: 11, border: 'none', cursor: selectedName ? 'pointer' : 'not-allowed' }}
              >
                Complete Encounter →
              </button>
            </div>
          </div>
        )}

        {step === 'complete' && (
          <div className="text-center">
            <div className="mb-6">
              <p style={{ fontFamily: "'Amiri', serif", fontSize: 36, color: '#c9a84c', marginBottom: 8 }}>الحمد لله</p>
              <p className="font-mono uppercase tracking-widest mb-2" style={{ color: '#2d7a47', fontSize: 12 }}>ALHAMDULILLAH — THE ARC IS COMPLETE</p>
              <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 13, lineHeight: 1.75, maxWidth: 480, margin: '0 auto' }}>
                You have completed your first Leaf Sequence encounter. You asked three questions, observed with intention, and extracted a Name. This is the IRL Lens in its simplest form — and it works on everything.
              </p>
            </div>

            {selectedName && (
              <div className="rounded-xl p-5 mb-6 text-left" style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.2)' }}>
                <p className="font-mono text-[9px] uppercase tracking-widest mb-2" style={{ color: 'rgba(201,168,76,0.5)' }}>YOUR EXTRACTED NAME</p>
                <p className="font-mono uppercase tracking-widest" style={{ color: '#c9a84c', fontSize: 14 }}>{selectedName}</p>
                {nameNote && <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 12, marginTop: 8, lineHeight: 1.6, fontStyle: 'italic' }}>"{nameNote}"</p>}
                <p style={{ color: 'rgba(212,197,169,0.4)', fontSize: 11, marginTop: 12, lineHeight: 1.6 }}>
                  Carry this Name with you. The Keys page will give you the vocabulary to go deeper — to understand how {selectedName} connects to the full framework of recognition.
                </p>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                href="/keys-to-kulli-haal"
                className="font-mono uppercase tracking-widest px-8 py-4 transition-all duration-200"
                style={{ background: '#c9a84c', color: '#0a1a12', fontSize: 12, letterSpacing: '0.15em' }}
              >
                Continue to Keys →
              </Link>
              <Link
                href="/names-reference"
                className="font-mono uppercase tracking-widest px-8 py-4 transition-all duration-200"
                style={{ border: '1px solid rgba(138,138,184,0.4)', color: '#8a8ab8', background: 'transparent', fontSize: 12, letterSpacing: '0.15em' }}
              >
                Explore Names Reference →
              </Link>
              <button
                onClick={() => { setStep('intro'); setAnswers({ q1: '', q2: '', q3: '' }); setSelectedName(null); setNameNote(''); }}
                className="font-mono uppercase tracking-widest px-8 py-4 transition-all duration-200"
                style={{ border: '1px solid rgba(45,122,71,0.3)', color: '#2d7a47', background: 'transparent', fontSize: 12, letterSpacing: '0.15em', cursor: 'pointer' }}
              >
                New Encounter →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── COMPANION VOICE LAYER ────────────────────────────────────────────────────
// Field notes register, not system register — the founder's voice walking alongside

interface CompanionNote {
  checkpointId: string;
  text: string;
  subtext?: string;
  type: 'field-note' | 'observation' | 'recognition';
}

const COMPANION_NOTES: CompanionNote[] = [
  {
    checkpointId: 'awareness',
    text: 'I\'d like to walk with you on a journey processing what I see, a bit of how I see it, and what I recognize on the way.',
    subtext: 'Not a tutorial. A walk. Come over here. Look at this.',
    type: 'field-note',
  },
  {
    checkpointId: 'orientation',
    text: 'A structure formed reflecting on sample examinations, commentary, and field notes. Understand the dimensions of a space, state, thing, its garment and grasping the measurements that it\'s given.',
    subtext: 'The framework is not invented — it is recognized.',
    type: 'observation',
  },
  {
    checkpointId: 'keys',
    text: 'The curriculum begins with the sense(s) and builds toward environmental responsibility. Form follows function on a journey to walk on some water.',
    subtext: 'I noticed the sand doing it too.',
    type: 'field-note',
  },
  {
    checkpointId: 'ayat',
    text: 'After some time, you\'re handed a lens, and we go around again, looking — seeing, recalling — recognizing.',
    subtext: 'Ilm Yaqeen → Ayn Yaqeen → Haqq Yaqeen.',
    type: 'recognition',
  },
  {
    checkpointId: 'names',
    text: 'The Creator, The Originator, The Fashioner, The Sovereign, is All-Knowing, All-Seeing, All-Hearing, All-Aware of His creations. He embodies the highest essence of all attributes.',
    subtext: 'Every attribute you perceive is a trace of an attribute that belongs absolutely to Allah.',
    type: 'observation',
  },
  {
    checkpointId: 'recognition',
    text: 'Go from aware to conscious, then act accordingly. Use the guidance around and within to best maintain that original balance.',
    subtext: 'The lens is in your hand. The forest is around you.',
    type: 'recognition',
  },
  {
    checkpointId: 'service',
    text: 'We are tasked with not transgressing in the balance, recognizing the signs in creation alongside the signs in revelation, and following the guidance.',
    subtext: 'Invite toward recognition, manifest appreciation and service.',
    type: 'field-note',
  },
];

function CompanionVoiceNote({ checkpointId }: { checkpointId: string }) {
  const note = COMPANION_NOTES.find(n => n.checkpointId === checkpointId);
  if (!note) return null;

  const typeColors: Record<CompanionNote['type'], string> = {
    'field-note': '#2d7a47',
    'observation': '#2d8a87',
    'recognition': '#c9a84c',
  };
  const typeLabels: Record<CompanionNote['type'], string> = {
    'field-note': '◎ FIELD NOTE',
    'observation': '⬡ OBSERVATION',
    'recognition': '✦ RECOGNITION',
  };

  const color = typeColors[note.type];

  return (
    <div style={{
      background: 'rgba(6,13,18,0.8)',
      border: `1px solid ${color}25`,
      borderLeft: `3px solid ${color}`,
      borderRadius: '0 8px 8px 0',
      padding: '16px 20px',
      marginTop: 20,
    }}>
      <div style={{ fontSize: 8, color: color, letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 10 }}>
        {typeLabels[note.type]} — FOUNDER'S THREAD
      </div>
      <p style={{
        fontFamily: "'Amiri', 'Georgia', serif",
        fontStyle: 'italic',
        fontSize: 14,
        color: '#d4c5a9',
        lineHeight: 1.8,
        marginBottom: note.subtext ? 8 : 0,
      }}>
        "{note.text}"
      </p>
      {note.subtext && (
        <p style={{ fontSize: 11, color: 'rgba(212,197,169,0.5)', lineHeight: 1.6, fontStyle: 'italic' }}>
          — {note.subtext}
        </p>
      )}
      <div style={{ marginTop: 10, paddingTop: 8, borderTop: `1px solid ${color}15` }}>
        <Link href="/open-letter" style={{ fontSize: 9, color: color, textDecoration: 'none', letterSpacing: '.08em' }}>
          → Read the Open Letter
        </Link>
      </div>
    </div>
  );
}

export default function GuidedWalkthroughPage() {
  const [completedIds, setCompletedIds] = useState<Set<string>>(new Set());
  const [activeId, setActiveId] = useState<string | null>('awareness');

  const toggleComplete = (id: string) => {
    setCompletedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
        const cp = CHECKPOINTS.find(c => c.id === id);
        if (cp) {
          const idx = CHECKPOINTS.findIndex(c => c.id === id);
          trackWalkthroughCheckpoint(id, cp.stage, idx, CHECKPOINTS.length).catch(() => {});
        }
      }
      return next;
    });
  };

  const progressPct = Math.round((completedIds.size / CHECKPOINTS.length) * 100);
  const activeCheckpoint = CHECKPOINTS.find(c => c.id === activeId) ?? CHECKPOINTS[0];

  return (
    <div className="min-h-screen deep-green-bg">
      <Header />
      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-20 px-6 overflow-hidden" style={{ background: 'linear-gradient(to bottom, #0d1a14, #1C2B25)' }}>
          <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'linear-gradient(rgba(139,105,20,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(139,105,20,0.04) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
          <div className="pixel-border-gold-top" />
          <div className="max-w-4xl mx-auto text-center relative z-10">
            <p className="font-mono text-xs tracking-widest uppercase mb-4" style={{ color: 'rgba(139,105,20,0.7)' }}>
              ✦ Guided Walkthrough · مسار موجَّه
            </p>
            <h1 className="font-mono text-5xl md:text-6xl font-black uppercase tracking-tight leading-none mb-6" style={{ color: '#F5F0E8' }}>
              FROM AWARE<br />
              <span className="text-shimmer">TO CONSCIOUS</span>
            </h1>
            <p className="font-mono text-sm max-w-xl mx-auto leading-relaxed mb-8" style={{ color: 'rgba(245,240,232,0.5)' }}>
              A guided journey for caretakers — from the Open Letter through the IRL Lens. Seven checkpoints. One recognition arc.
            </p>
            {/* Progress bar */}
            <div className="max-w-md mx-auto">
              <div className="flex items-center justify-between mb-2">
                <span className="font-mono text-[10px] tracking-widest uppercase" style={{ color: 'rgba(139,105,20,0.6)' }}>Journey Progress</span>
                <span className="font-mono text-[10px]" style={{ color: '#D4A82A' }}>{completedIds.size}/{CHECKPOINTS.length} checkpoints</span>
              </div>
              <div className="h-1 rounded-full overflow-hidden" style={{ background: 'rgba(139,105,20,0.15)' }}>
                <div
                  className="h-full rounded-full transition-all duration-700"
                  style={{ width: `${progressPct}%`, background: 'linear-gradient(to right, #8B6914, #D4A82A)' }}
                />
              </div>
              {progressPct === 100 && (
                <p className="font-mono text-[10px] tracking-widest uppercase mt-3" style={{ color: '#2d7a47' }}>
                  ✦ Alhamdulillah — The arc is complete
                </p>
              )}
            </div>
          </div>
        </section>

        {/* Main two-column layout */}
        <section className="py-16 px-6" style={{ background: '#0d1a14' }}>
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Left: Checkpoint list */}
              <div className="lg:w-72 flex-shrink-0">
                <div className="sticky top-24 space-y-2">
                  {CHECKPOINTS.map((cp, idx) => {
                    const isActive = activeId === cp.id;
                    const isCompleted = completedIds.has(cp.id);
                    return (
                      <button
                        key={cp.id}
                        onClick={() => setActiveId(cp.id)}
                        className="w-full text-left rounded-lg p-4 transition-all duration-200"
                        style={{
                          background: isActive ? `${cp.color}10` : 'rgba(28,43,37,0.4)',
                          border: `1px solid ${isActive ? cp.color + '40' : 'rgba(44,74,62,0.3)'}`,
                          borderLeft: `3px solid ${isCompleted ? cp.color : isActive ? cp.color + '80' : 'transparent'}`,
                        }}
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className="w-7 h-7 rounded-full flex items-center justify-center font-mono text-xs font-black flex-shrink-0"
                            style={{
                              background: isCompleted ? cp.color : `${cp.color}15`,
                              border: `1px solid ${cp.color}40`,
                              color: isCompleted ? '#0d1a14' : cp.color,
                            }}
                          >
                            {isCompleted ? '✓' : cp.stageNum}
                          </div>
                          <div>
                            <p className="font-mono text-[9px] uppercase tracking-widest" style={{ color: isActive ? cp.color : 'rgba(245,240,232,0.4)' }}>{cp.stage}</p>
                            <p className="font-mono text-[10px] font-bold" style={{ color: isActive ? '#F5F0E8' : 'rgba(245,240,232,0.5)' }}>{cp.title}</p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Right: Active checkpoint detail */}
              <div className="flex-1 min-w-0">
                {activeCheckpoint && (
                  <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${activeCheckpoint.color}20` }}>
                    {/* Header */}
                    <div className="px-8 py-6" style={{ background: `${activeCheckpoint.color}08`, borderBottom: `1px solid ${activeCheckpoint.color}15` }}>
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className="font-mono text-[9px] uppercase tracking-widest mb-2" style={{ color: `${activeCheckpoint.color}70` }}>
                            Stage {activeCheckpoint.stageNum} · {activeCheckpoint.stage} · {activeCheckpoint.stageAr}
                          </p>
                          <h2 className="font-mono text-2xl font-black uppercase tracking-tight mb-1" style={{ color: '#F5F0E8' }}>
                            {activeCheckpoint.title}
                          </h2>
                          <p className="font-mono text-xs uppercase tracking-widest" style={{ color: activeCheckpoint.color }}>
                            {activeCheckpoint.subtitle}
                          </p>
                        </div>
                        <button
                          onClick={() => toggleComplete(activeCheckpoint.id)}
                          className="font-mono text-[9px] uppercase tracking-widest px-4 py-2 rounded transition-all flex-shrink-0"
                          style={{
                            background: completedIds.has(activeCheckpoint.id) ? `${activeCheckpoint.color}20` : 'transparent',
                            border: `1px solid ${activeCheckpoint.color}40`,
                            color: activeCheckpoint.color,
                          }}
                        >
                          {completedIds.has(activeCheckpoint.id) ? '✓ Complete' : 'Mark Complete'}
                        </button>
                      </div>
                    </div>

                    <div className="p-8 space-y-6">
                      {/* Description */}
                      <p style={{ color: '#d4c5a9', fontSize: 15, lineHeight: 1.85 }}>{activeCheckpoint.description}</p>

                      {/* Verse */}
                      {activeCheckpoint.verse && (
                        <div className="rounded-xl p-5" style={{ background: 'rgba(201,168,76,0.05)', border: '1px solid rgba(201,168,76,0.15)' }}>
                          <p style={{ fontFamily: "'Amiri', serif", fontSize: 20, color: '#c9a84c', direction: 'rtl', textAlign: 'right', marginBottom: 8 }}>
                            {activeCheckpoint.verse.arabic}
                          </p>
                          <p className="italic" style={{ color: 'rgba(212,197,169,0.7)', fontSize: 13 }}>
                            "{activeCheckpoint.verse.translation}" — ({activeCheckpoint.verse.ref})
                          </p>
                        </div>
                      )}

                      {/* Explanation */}
                      <div>
                        <p className="font-mono text-[9px] uppercase tracking-widest mb-3" style={{ color: 'rgba(212,197,169,0.4)' }}>DEEPER CONTEXT</p>
                        <p style={{ color: 'rgba(212,197,169,0.7)', fontSize: 13, lineHeight: 1.8 }}>{activeCheckpoint.explanation}</p>
                      </div>

                      {/* ── COMPANION VOICE — field notes register ── */}
                      <CompanionVoiceNote checkpointId={activeCheckpoint.id} />

                      {/* Key Terms */}
                      <div>
                        <p className="font-mono text-[9px] uppercase tracking-widest mb-3" style={{ color: 'rgba(212,197,169,0.4)' }}>KEY TERMS AT THIS STAGE</p>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          {activeCheckpoint.keyTerms.map(kt => (
                            <div key={kt.term} className="rounded-lg p-4" style={{ background: `${activeCheckpoint.color}06`, border: `1px solid ${activeCheckpoint.color}15` }}>
                              <p className="font-mono text-[10px] uppercase tracking-widest mb-2" style={{ color: activeCheckpoint.color }}>{kt.term}</p>
                              <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 11, lineHeight: 1.5 }}>{kt.def}</p>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* CTA */}
                      <div className="flex gap-3 flex-wrap pt-2">
                        <Link
                          href={activeCheckpoint.href}
                          className="font-mono uppercase tracking-widest px-6 py-3 transition-all duration-200"
                          style={{ background: activeCheckpoint.color, color: '#0d1a14', fontSize: 11, letterSpacing: '0.15em' }}
                        >
                          Visit {activeCheckpoint.title} →
                        </Link>
                        {activeCheckpoint.id !== CHECKPOINTS[CHECKPOINTS.length - 1].id && (
                          <button
                            onClick={() => {
                              const idx = CHECKPOINTS.findIndex(c => c.id === activeCheckpoint.id);
                              if (idx < CHECKPOINTS.length - 1) setActiveId(CHECKPOINTS[idx + 1].id);
                            }}
                            className="font-mono uppercase tracking-widest px-6 py-3 transition-all duration-200"
                            style={{ border: `1px solid ${activeCheckpoint.color}40`, color: activeCheckpoint.color, background: 'transparent', fontSize: 11, cursor: 'pointer' }}
                          >
                            Next Stage →
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* ── FIRST LEAF SEQUENCE ENCOUNTER ── */}
        <section className="py-20 px-6" style={{ background: '#060d12' }}>
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <p className="font-mono text-[10px] tracking-widest uppercase mb-3" style={{ color: 'rgba(45,122,71,0.5)' }}>
                ✦ YOUR FIRST ENCOUNTER · أول لقاء
              </p>
              <h2 className="font-mono text-3xl font-black uppercase tracking-tight mb-4" style={{ color: '#F5F0E8' }}>
                THE LEAF SEQUENCE
              </h2>
              <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 15, fontStyle: 'italic', maxWidth: 560, margin: '0 auto' }}>
                Before you continue to the Keys, walk through your first IRL Lens encounter. Three questions. One Name. The recognition arc in its simplest form.
              </p>
            </div>
            <LeafSequenceEncounter />

            {/* ── HAWAS CALIBRATION COMPLETE → HANDED A LENS ── */}
            <div style={{ marginTop: 48 }}>
              <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <p className="font-mono text-[10px] tracking-widest uppercase mb-3" style={{ color: 'rgba(45,138,135,0.5)' }}>
                  ◎ AFTER SOME TIME · بعد مرور الوقت
                </p>
                <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 13, fontStyle: 'italic', maxWidth: 480, margin: '0 auto' }}>
                  You have been looking. You have been noticing. A thought caught between fingertips, stretched out, examined. Now — something is placed in your hands.
                </p>
              </div>
              <HandedALensMoment />
            </div>
          </div>
        </section>

        {/* ── DEPOSIT AT KEYS ── */}
        <section className="py-20 px-6" style={{ background: '#0d1a14' }}>
          <div className="max-w-4xl mx-auto">
            <div className="rounded-xl p-8 text-center" style={{ background: 'rgba(201,168,76,0.05)', border: '1px solid rgba(201,168,76,0.2)' }}>
              <p className="font-mono text-[10px] uppercase tracking-widest mb-4" style={{ color: 'rgba(201,168,76,0.4)' }}>
                YOU ARE READY · أنت مستعد
              </p>
              <p style={{ fontFamily: "'Amiri', serif", fontSize: 28, color: '#c9a84c', marginBottom: 12 }}>
                اهْدِنَا الصِّرَاطَ الْمُسْتَقِيمَ
              </p>
              <p className="italic mb-6" style={{ color: 'rgba(212,197,169,0.6)', fontSize: 14 }}>
                "Guide us to the straight path." — (Al-Fatiha 1:6)
              </p>
              <p style={{ color: '#d4c5a9', fontSize: 14, lineHeight: 1.85, maxWidth: 560, margin: '0 auto 24px' }}>
                You have walked the recognition arc. You have asked the three questions. You have extracted a Name. You now have the vocabulary to enter the Keys — the seven conceptual frameworks that give this practice its full depth.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link
                  href="/keys-to-kulli-haal"
                  className="font-mono uppercase tracking-widest px-10 py-4 transition-all duration-200"
                  style={{ background: '#c9a84c', color: '#0a1a12', fontSize: 12, letterSpacing: '0.15em' }}
                >
                  Enter the Keys →
                </Link>
                <Link
                  href="/inner-reality-lens"
                  className="font-mono uppercase tracking-widest px-10 py-4 transition-all duration-200"
                  style={{ border: '1px solid rgba(45,138,135,0.4)', color: '#2d8a87', background: 'transparent', fontSize: 12, letterSpacing: '0.15em' }}
                >
                  Open IRL Lens →
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
